import { ExternalLink, FileText, Pin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, toQuery } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, LiveDot, Loading, StatusBadge } from '../components/base';
import { SeriesChart } from '../components/charts';
import CapacityPanel from '../components/CapacityPanel';
import { Kpi, KpiGrid } from '../components/Kpi';
import {
  CATEGORY_LABELS, STATUS_LABELS, formatDate, formatDecimal, formatNumber,
  isRateMetric, kpiRank, metricLabel,
} from '../formats';
import type { Client, DashboardData } from '../types';

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const isOwner = user?.role === 'owner';

  /* The client filter is the URL (?client=<id>) rather than component state,
     so that a client has an address: Admin > Clients links here, and a
     filtered dashboard survives a reload and can be sent to somebody. Only
     an owner has a filter to keep; a client login is scoped by the API from
     the database whatever the query string says, so the param is not even
     sent for them. */
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = isOwner ? (searchParams.get('client') ?? '') : '';
  const setClientId = (id: string) => setSearchParams(id ? { client: id } : {}, { replace: true });

  useEffect(() => {
    if (!isOwner) return;
    get<{ clients: Client[] }>('/clients').then(({ clients: c }) => setClients(c)).catch(() => {});
  }, [isOwner]);

  useEffect(() => {
    setData(null);
    setError(null);
    get<DashboardData>(`/dashboard${toQuery({ client_id: clientId })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [clientId, reloadKey]);

  if (error) return <ErrorBox message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!data) return <Loading />;

  // The headline tiles: sums for counts, averages for rates, capped at four,
  // traffic story first.
  const kpis = [...data.kpis]
    .sort((a, b) => kpiRank(a.metric) - kpiRank(b.metric) || a.metric.localeCompare(b.metric))
    .slice(0, 4);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
            {formatDate(data.period.from)} – {formatDate(data.period.to)}, compared with the 30 days before
          </p>
        </div>
        {isOwner && (
          <div>
            <label htmlFor="client-filter" className="form-label">Client</label>
            <select
              id="client-filter" className="field w-56"
              value={clientId} onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {kpis.length > 0 ? (
        <KpiGrid>
          {kpis.map((k) => (
            <Kpi
              key={k.metric}
              label={metricLabel(k.metric)}
              value={isRateMetric(k.metric) ? formatDecimal(k.average) : formatNumber(k.total)}
              sub={isRateMetric(k.metric) ? '30-day average' : 'last 30 days'}
              change={isRateMetric(k.metric) ? null : k.change_pct}
            />
          ))}
        </KpiGrid>
      ) : (
        <div className="card">
          <Empty
            title="No performance data yet"
            description="Metrics appear here once your product starts reporting."
          />
        </div>
      )}

      {data.visits_series.length > 0 && (
        <section className="card p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
            Visits per day - last 60 days
          </h2>
          <SeriesChart points={data.visits_series} />
        </section>
      )}

      {isOwner && <CapacityPanel />}

      <section>
        <h2 className="mb-3 text-lg font-semibold">Your products</h2>
        {data.products.length === 0 ? (
          <div className="card"><Empty title="No products yet" /></div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.products.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} className="card block p-5 transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{p.name}</h3>
                    {isOwner && (
                      <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">{p.client_name}</p>
                    )}
                  </div>
                  <span className="flex flex-col items-end gap-1.5">
                    <StatusBadge status={p.status} label={STATUS_LABELS[p.status] ?? p.status} />
                    <LiveDot check={p.last_check} />
                  </span>
                </div>
                <dl className="mt-3 space-y-1 text-sm text-ink-600 dark:text-paper-300">
                  {p.url && (
                    <div className="flex items-center gap-1.5">
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-400" aria-hidden />
                      <span className="truncate">{p.url.replace(/^https?:\/\//, '')}</span>
                    </div>
                  )}
                  {p.launched_at && <div>Launched {formatDate(p.launched_at)}</div>}
                </dl>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Recent documents</h2>
        {data.recent_documents.length === 0 ? (
          <div className="card"><Empty title="Nothing published yet" /></div>
        ) : (
          <div className="card divide-y divide-paper-200 dark:divide-ink-700">
            {data.recent_documents.map((d) => (
              <Link
                key={d.id}
                to={`/documents/${d.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-paper-50 dark:hover:bg-ink-700/50"
              >
                <FileText className="h-4 w-4 shrink-0 text-ink-400" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{d.title}</p>
                  <p className="text-xs text-ink-500 dark:text-ink-400">
                    {CATEGORY_LABELS[d.category]}
                    {d.product_name ? ` · ${d.product_name}` : ''} · {formatDate(d.updated_at)}
                  </p>
                </div>
                {d.pinned === 1 && <Pin className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden />}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
